/**
 * The correction form as typed, and the checks it makes before sending (DECISIONS.md #43).
 *
 * The same shape as `registrationDraft.ts` and deliberately so — one set of rules, two forms — with
 * one structural difference: **a correction draft starts from what the record says, not from
 * empty.** {@link draftFromCurrent} fills it in, so the clinician is editing the identity rather
 * than retyping it, and a field they do not touch is sent back unchanged.
 *
 * `MAXIMUM_FIELD_LENGTH` and `OFFERED_SEXES` are imported rather than restated. They are the same
 * limits the same columns have, and a second copy is a copy that can drift.
 */

import type { PatientOverview } from './patientOverviewApi'
import { MAXIMUM_FIELD_LENGTH, OFFERED_SEXES } from './registrationDraft'

/** The three text fields, in the order they are rendered. `sex` and the flag are their own controls. */
export const CORRECTION_FIELDS = ['givenName', 'familyName', 'birthDate'] as const

export type CorrectionField = (typeof CORRECTION_FIELDS)[number]

/**
 * The fields the length check covers, in the order the server checks them.
 *
 * Only the two free-text ones. `birthDate` is bounded by its format and `sex` by its vocabulary,
 * so a length check on either would refuse before the check that actually names the mistake.
 */
const LENGTH_CHECKED_FIELDS = ['givenName', 'familyName'] as const

/**
 * The form's state.
 *
 * Strings and never null, including `sex`, which is `''` for "nobody has answered". That is the
 * same three-state handling the registration form has, and it matters more here: a patient whose
 * `sex` is null in the record must be able to stay null after a correction of their name.
 */
export interface CorrectionDraft {
  readonly givenName: string
  readonly familyName: string

  /** `YYYY-MM-DD` as typed. Never a native date input — see `PatientRegistrationScreen`. */
  readonly birthDate: string

  /** An `OFFERED_SEXES` code, or `''` for unanswered. */
  readonly sex: string

  readonly isEstimatedIdentity: boolean
}

/**
 * The draft the form opens with: the identity exactly as the record holds it.
 *
 * **Pre-filled, unlike `emptyDraft()`.** A correction form that started blank would make every
 * correction look like five changes, and a clinician correcting a spelling would have to retype a
 * birth date somebody else established — with no way to tell, afterwards, which of the two of them
 * was wrong about it.
 *
 * @param patient The overview being corrected.
 */
export function draftFromCurrent(patient: PatientOverview): CorrectionDraft {
  return {
    givenName: patient.givenName ?? '',
    familyName: patient.familyName ?? '',
    birthDate: patient.birthDate ?? '',
    sex: patient.sex ?? '',
    isEstimatedIdentity: patient.isEstimatedIdentity,
  }
}

/**
 * Whether the draft says anything the given starting point did not.
 *
 * Compared against the record rather than against empty, which is what makes it mean two things at
 * once: it is the unsaved-work guard **and** the client-side half of `CORRECTION_NO_CHANGE`. The
 * comparison trims, because trailing whitespace is not a correction — `toSubmission` would send the
 * trimmed value and the server would refuse the same form as a no-op.
 *
 * @param draft The form as typed.
 * @param original The draft {@link draftFromCurrent} produced for the same patient.
 */
export function isDraftDirty(draft: CorrectionDraft, original: CorrectionDraft): boolean {
  return (
    draft.givenName.trim() !== original.givenName.trim() ||
    draft.familyName.trim() !== original.familyName.trim() ||
    draft.birthDate.trim() !== original.birthDate.trim() ||
    draft.sex.trim() !== original.sex.trim() ||
    draft.isEstimatedIdentity !== original.isEstimatedIdentity
  )
}

/**
 * A problem the form found, in the same shape a refusal arrives in.
 *
 * One code and one set of params, so a client-side problem renders through exactly the message the
 * server would have sent for the same mistake.
 */
export interface CorrectionDraftProblem {
  readonly code:
    | 'CORRECTION_FAMILY_NAME_MISSING'
    | 'CORRECTION_FIELD_TOO_LONG'
    | 'CORRECTION_SEX_UNKNOWN'
    | 'CORRECTION_BIRTH_DATE_MALFORMED'
    | 'CORRECTION_BIRTH_DATE_IN_FUTURE'
    | 'CORRECTION_NO_CHANGE'

  readonly params: {
    readonly field?: string
    readonly max?: number
    readonly sex?: string
    readonly birthDate?: string
  }
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/**
 * The first thing wrong with the draft, or null.
 *
 * **The order matches the server's**, which is what makes the two agree about *which* problem a
 * form with several has: `PatientCorrection.Validate`'s four field rules, and then
 * `CORRECTION_NO_CHANGE`, which the service checks after validation. The screen shows one thing to
 * fix at a time, so the order is part of the behaviour and not an implementation detail.
 *
 * `CORRECTION_SUPERSEDES_MISSING`, `PATIENT_NOT_FOUND`, `PATIENT_RETRACTED` and
 * `CORRECTION_VERSION_STALE` are deliberately absent. The first cannot happen — the form always
 * has the `versionId` it was opened with — and the other three are facts about the record that only
 * the server can know.
 *
 * @param draft The form as typed.
 * @param original The draft {@link draftFromCurrent} produced, for the no-change check.
 * @param today The clinical today, injected so the birth-date check is testable and so it agrees
 *   with the clock the record is written against.
 */
export function correctionProblem(
  draft: CorrectionDraft,
  original: CorrectionDraft,
  today: Date,
): CorrectionDraftProblem | null {
  if (draft.familyName.trim().length === 0 && !draft.isEstimatedIdentity) {
    return { code: 'CORRECTION_FAMILY_NAME_MISSING', params: {} }
  }

  for (const field of LENGTH_CHECKED_FIELDS) {
    if (draft[field].trim().length > MAXIMUM_FIELD_LENGTH) {
      return {
        code: 'CORRECTION_FIELD_TOO_LONG',
        params: { field, max: MAXIMUM_FIELD_LENGTH },
      }
    }
  }

  if (draft.sex !== '' && !(OFFERED_SEXES as readonly string[]).includes(draft.sex)) {
    return { code: 'CORRECTION_SEX_UNKNOWN', params: { sex: draft.sex } }
  }

  const birthDate = draft.birthDate.trim()

  if (birthDate !== '') {
    // Exact ISO, and then a real date. `Date.parse` alone would accept `1950-02-30` and quietly
    // move it to March, and a birth date that is silently wrong is visible nowhere on the screen
    // (I18N.md §3).
    if (!ISO_DATE.test(birthDate)) {
      return { code: 'CORRECTION_BIRTH_DATE_MALFORMED', params: { birthDate } }
    }

    const parsed = new Date(`${birthDate}T00:00:00Z`)

    if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== birthDate) {
      return { code: 'CORRECTION_BIRTH_DATE_MALFORMED', params: { birthDate } }
    }

    // Compared as calendar days in UTC, matching the server's `DateOnly` check.
    const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())

    if (parsed.getTime() > todayUtc) {
      return { code: 'CORRECTION_BIRTH_DATE_IN_FUTURE', params: { birthDate } }
    }
  }

  // Last, as on the server. A form with a real mistake in it is told about the mistake rather than
  // about having changed nothing — which would be true and useless.
  if (!isDraftDirty(draft, original)) {
    return { code: 'CORRECTION_NO_CHANGE', params: {} }
  }

  return null
}

/**
 * The draft as the server wants it.
 *
 * Trims, and turns a field that is empty or only whitespace into `null`. The server does this
 * again; the point of doing it here is that what was checked above is what is sent.
 *
 * @param draft The form as typed.
 * @param supersedes The `version_id` the form was opened on — `PatientOverview.versionId`.
 */
export function toSubmission(
  draft: CorrectionDraft,
  supersedes: string,
): {
  readonly supersedes: string
  readonly givenName: string | null
  readonly familyName: string | null
  readonly birthDate: string | null
  readonly sex: string | null
  readonly isEstimatedIdentity: boolean
} {
  const orNull = (value: string): string | null => {
    const trimmed = value.trim()
    return trimmed.length === 0 ? null : trimmed
  }

  return {
    supersedes,
    givenName: orNull(draft.givenName),
    familyName: orNull(draft.familyName),
    birthDate: orNull(draft.birthDate),
    sex: orNull(draft.sex),
    isEstimatedIdentity: draft.isEstimatedIdentity,
  }
}
